import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Field, FieldArray, useFormikContext } from 'formik';
import { connect } from 'react-redux';
import axios from 'axios';
import get from 'lodash/get';
import { PorletHeaderTitle } from 'components/porletHeader';
import { getDistributorsOptions } from 'redux/distributors/selector';
import { getCountryProfile } from 'redux/profile/selector';
import { requests as distributorsRequest } from 'redux/distributors/reducer';
import TextField from 'components/form/input';
import Autocomplete from 'components/form/autocomplete';
import '../../newFasara/form/step3/previewer.scss';

import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';

const API_URL = window.env.API_URL;
const ACCESS_TOKEN = window.env.ACCESS_TOKEN;

const CUSTOM_FAMILY_OPTIONS = [{ value: 'Custom', label: 'Custom' }];

const newSheet = (data = {}) => ({
  type: '',
  meters: '',
  distributor: data?.distributor ?? {},
  custom: '',
  glassFinish: null,
  film: null,
  family: null,
  run: '',
});

export const initialValues = {
  data: {
    ...newSheet(),
  },
};

function authHeaders() {
  return {
    Authorization: `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`,
  };
}

function normalizeFamiliesPayload(data) {
  const list = Array.isArray(data) ? data : data?.data;
  return Array.isArray(list) ? list : [];
}

function mapFamilyOption(row) {
  const value = row.id ?? row.value ?? row.code;
  return {
    label: row.name ?? row.label ?? row.code ?? '',
    value,
    type: row.type,
  };
}

async function fetchFasaraGlassCategories() {
  const url = `${API_URL}/family-categories/FASARA`;
  try {
    const response = await axios.get(url, {
      headers: authHeaders(),
    });
    return normalizeFamiliesPayload(response.data)
      .map(mapFamilyOption)
      .filter((o) => o.value != null && o.value !== '');
  } catch (err) {
    console.error('fetchFasaraGlassCategories', err);
    return [];
  }
}

async function fetchFasaraFamiliesForCategory(categoryValue) {
  const url = `${API_URL}/families/arqfinishes`;
  try {
    const response = await axios.get(url, {
      params: { type: 'FASARA', category: categoryValue },
      headers: authHeaders(),
    });
    return normalizeFamiliesPayload(response.data)
      .map(mapFamilyOption)
      .filter((o) => o.value != null && o.value !== '');
  } catch (err) {
    console.error('fetchFasaraFamiliesForCategory', err);
    return [];
  }
}

function firstNonemptyString(...candidates) {
  for (const v of candidates) {
    if (v != null && String(v).trim() !== '') {
      return v;
    }
  }
  return undefined;
}

function normalizeFasaraFilmsPayload(data, family) {
  const list = Array.isArray(data) ? data : data?.data;
  if (!Array.isArray(list)) {
    return [];
  }
  return list
    .map((item) => {
      const label = item.label ?? item.name ?? '';
      const value = item.value ?? item.code ?? item.id ?? '';
      const itemFamily = item.family ?? family;
      return {
        label,
        value,
        family: itemFamily,
        externalThumbnailUrl: firstNonemptyString(
          item.externalThumbnailUrl,
          item.external_thumbnail_url,
        ),
        externalImageUrl: firstNonemptyString(
          item.externalImageUrl,
          item.external_image_url,
        ),
      };
    })
    .filter((item) => item.value !== '' && item.value != null);
}

function findFasaraProductRow(familyProducts, film) {
  if (!Array.isArray(familyProducts) || film == null || film === '') {
    return null;
  }
  return familyProducts.find((p) => {
    const keys = [p.id, p.value, p.code, p.name, p.label].filter(
      (k) => k != null && k !== '',
    );
    return keys.some((k) => String(k) === String(film));
  });
}

function getFasaraLocalFilmPreviewSrc({ film, familyProducts, filmData }) {
  const publicBase = process.env.PUBLIC_URL || '';
  if (!film) {
    return `${publicBase}/media/fasara/Choose.jpg`;
  }
  if (film === 'Custom') {
    return `${publicBase}/media/fasara/Custom.jpg`;
  }
  const row =
    findFasaraProductRow(familyProducts, film) ??
    findFasaraProductRow(filmData ? [filmData] : [], film);
  const base =
    row &&
    (row.code ??
      row.filmCode ??
      row.sku ??
      (typeof row.value === 'string' ? row.value : null) ??
      (typeof row.name === 'string' ? row.name.trim() || null : null) ??
      (typeof row.label === 'string' ? row.label.trim() || null : null));
  const file =
    base != null && String(base).trim() !== ''
      ? `${String(base).trim()}.jpg`
      : `${String(film)}.jpg`;
  return `${publicBase}/media/fasara/${file}`;
}

function firstFasaraExternalPreviewUrl(match) {
  return firstNonemptyString(
    match?.externalThumbnailUrl,
    match?.external_thumbnail_url,
    match?.externalImageUrl,
    match?.external_image_url,
    match?.externalImageURL,
  );
}

function getFasaraSheetPreviewSrc({ film, familyProducts, filmData }) {
  const localSrc = getFasaraLocalFilmPreviewSrc({
    film,
    familyProducts,
    filmData,
  });
  if (!film || film === 'Custom') {
    return localSrc;
  }
  const match =
    findFasaraProductRow(familyProducts, film) ??
    findFasaraProductRow(filmData ? [filmData] : [], film);
  return firstFasaraExternalPreviewUrl(match) ?? localSrc;
}

async function fetchFasaraProductsByFamily(family) {
  const url = `${API_URL}/films/arqfinishes`;
  try {
    const response = await axios.get(url, {
      params: { family },
      headers: authHeaders(),
    });
    let items = normalizeFasaraFilmsPayload(response.data, family);
    const narrowed = items.filter((item) => {
      if (item.family == null && item.familyId == null && item.line == null) {
        return true;
      }
      return (
        String(item.family) === String(family) ||
        String(item.familyId) === String(family)
      );
    });
    if (narrowed.length > 0) {
      items = narrowed;
    }
    return items.sort((a, b) => String(a.label).localeCompare(String(b.label)));
  } catch (err) {
    console.error('fetchFasaraProductsByFamily', err);
    return [];
  }
}

function filmOptionsForRow(family, loadedProducts, savedFilm, filmData) {
  if (!family) {
    return [];
  }
  if (family === 'Custom') {
    return [{ value: 'Custom', label: 'Custom' }];
  }

  const filmStr =
    savedFilm != null && savedFilm !== '' ? String(savedFilm).trim() : '';
  const savedLabel = filmData?.name != null ? String(filmData.name) : filmStr;

  const fromApi = Array.isArray(loadedProducts)
    ? loadedProducts.map((item) => ({
        label: item.label,
        value: item.label,
        externalThumbnailUrl: item.externalThumbnailUrl,
        externalImageUrl: item.externalImageUrl,
      }))
    : [];

  if (filmStr && !fromApi.some((o) => String(o.value) === filmStr)) {
    return [{ value: filmStr, label: savedLabel || filmStr }, ...fromApi];
  }
  return fromApi;
}

const fasaraWinSheetsMapStateToProps = (state) => ({
  distributorsOptions: getDistributorsOptions(state),
});

const fasaraWinSheetsMapDispatchToProps = (dispatch) => ({
  fetchDistributors: () => dispatch(distributorsRequest.fetch()),
});

const FasaraWinApplicationRow = ({
  index,
  values,
  categoriesOptions,
  categoriesLoading,
  filteredOptions,
  onRemove,
  showRemoveButton,
}) => {
  const { setFieldValue } = useFormikContext();
  const sheet = values.data.applications[index];
  const glassFinish = sheet?.glassFinish;
  const family = sheet?.family;

  const prevCategoryRef = useRef(glassFinish);
  const prevFamilyRef = useRef(family);
  const [familyProducts, setFamilyProducts] = useState(undefined);
  const [familyOptionsList, setFamilyOptionsList] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(false);

  const categorySelectOptions = useMemo(() => {
    const cats = Array.isArray(categoriesOptions) ? categoriesOptions : [];
    const base = [...cats, { value: 'Custom', label: 'Custom' }];
    const g = sheet?.glassFinish;
    if (g == null || g === '') {
      return base;
    }
    const gs = String(g);
    if (!base.some((o) => String(o.value) === gs)) {
      return [{ value: g, label: String(g) }, ...base];
    }
    return base;
  }, [categoriesOptions, sheet?.glassFinish]);

  const familySelectOptions = useMemo(() => {
    const list = Array.isArray(familyOptionsList) ? familyOptionsList : [];
    const base = [...list];
    const f = sheet?.family;
    if (
      f != null &&
      f !== '' &&
      !base.some((o) => String(o.value) === String(f))
    ) {
      return [{ value: f, label: String(f) }, ...base];
    }
    return base;
  }, [familyOptionsList, sheet?.family]);

  useEffect(() => {
    let cancelled = false;

    if (!glassFinish) {
      setFamilyOptionsList([]);
      setFamiliesLoading(false);
      return undefined;
    }
    if (glassFinish === 'Custom') {
      setFamilyOptionsList(CUSTOM_FAMILY_OPTIONS);
      setFamiliesLoading(false);
      return undefined;
    }

    setFamiliesLoading(true);
    setFamilyOptionsList([]);
    fetchFasaraFamiliesForCategory(glassFinish).then((opts) => {
      if (!cancelled) {
        setFamilyOptionsList(Array.isArray(opts) ? opts : []);
        setFamiliesLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [glassFinish]);

  useEffect(() => {
    const prev = prevCategoryRef.current;
    if (prev != null && glassFinish != null && prev !== glassFinish) {
      setFieldValue(`data.applications[${index}].family`, null);
      setFieldValue(`data.applications[${index}].film`, null);
      setFieldValue(`data.applications[${index}].custom`, '');
    }
    prevCategoryRef.current = glassFinish;
  }, [glassFinish, index, setFieldValue]);

  useEffect(() => {
    const prev = prevFamilyRef.current;
    if (prev != null && family != null && prev !== family) {
      setFieldValue(`data.applications[${index}].film`, null);
      setFieldValue(`data.applications[${index}].custom`, '');
    }
    prevFamilyRef.current = family;
  }, [family, index, setFieldValue]);

  useEffect(() => {
    let cancelled = false;

    if (!family || !glassFinish) {
      setFamilyProducts(undefined);
      return undefined;
    }
    if (family === 'Custom') {
      setFamilyProducts(undefined);
      return undefined;
    }
    if (
      familySelectOptions.length > 0 &&
      !familySelectOptions.some((o) => String(o.value) === String(family))
    ) {
      setFamilyProducts(undefined);
      return undefined;
    }

    setFamilyProducts(null);
    fetchFasaraProductsByFamily(family).then((items) => {
      if (!cancelled) {
        setFamilyProducts(Array.isArray(items) ? items : []);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [glassFinish, family, familySelectOptions]);

  const filmDisabled =
    !glassFinish || !family || (family !== 'Custom' && familyProducts == null);

  const previewSrc = getFasaraSheetPreviewSrc({
    film: sheet?.film,
    familyProducts,
    filmData: sheet?.filmData,
  });

  return (
    <div>
      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="Categoría" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <Field
            name={`data.applications[${index}].glassFinish`}
            component={Autocomplete}
            variant="outlined"
            translate="false"
            disabled={categoriesLoading}
            options={categorySelectOptions}
          />
        </div>
      </div>
      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="Familia de producto" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <Field
            name={`data.applications[${index}].family`}
            component={Autocomplete}
            variant="outlined"
            translate="false"
            disabled={!glassFinish || categoriesLoading || familiesLoading}
            options={familySelectOptions}
          />
        </div>
      </div>
      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="Producto" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <Field
            name={`data.applications[${index}].film`}
            component={Autocomplete}
            variant="outlined"
            translate="false"
            disabled={filmDisabled}
            options={filmOptionsForRow(
              family,
              familyProducts,
              sheet?.film,
              sheet?.filmData,
            )}
            textFieldProps={{
              onBlur: () => {
                const row = values.data.applications[index];
                const keepCustom =
                  row?.film == 'Custom' && row?.family == 'Custom';
                if (!keepCustom) {
                  setFieldValue(`data.applications[${index}].custom`, '');
                }
              },
            }}
          />
        </div>
      </div>
      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="M2 Necesarios: *" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <Field
            name={`data.applications[${index}].meters`}
            component={TextField}
            type="text"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>
      </div>
      {sheet.custom === '' ? (
        <></>
      ) : (
        <div className="row FormRow">
          <div className="col-sm-12 col-md-12 col-lg-4">
            <FormattedMessage id="Descripción" />
          </div>
          <div className="col-sm-12 col-md-12 col-lg-8">
            <Field
              name={`data.applications[${index}].custom`}
              component={TextField}
              type="text"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </div>
      )}
      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="Vista Previa" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <img
            src={previewSrc}
            className="img-previewer-summary w-100 "
            alt=""
          />
        </div>
      </div>

      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="Distribuidor: *" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <Field
            name={`data.applications[${index}].distributor.id`}
            component={Autocomplete}
            variant="outlined"
            translate={false}
            options={filteredOptions}
            placeholder="Seleccionar"
            InputLabelProps={{ shrink: true }}
          />
        </div>
      </div>
      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="Número de RUN: *" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <Field
            name={`data.applications[${index}].run`}
            component={TextField}
            type="text"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>
      </div>

      {showRemoveButton && (
        <div className="row">
          <div className="col-12 kt-space-20" />
          <div className="col-12">
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={onRemove}
            >
              <DeleteIcon color="primary" />
              <FormattedMessage id="Eliminar esta lámina" />
            </Button>
            <div className="col-12 kt-space-12" />
          </div>
        </div>
      )}
    </div>
  );
};

const FasaraWinApplicationsInner = ({
  values,
  country,
  distributorsOptions,
  fetchDistributors,
}) => {
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    fetchDistributors();
  }, [fetchDistributors]);

  useEffect(() => {
    let cancelled = false;
    fetchFasaraGlassCategories().then((items) => {
      if (!cancelled) {
        setCategoriesOptions(Array.isArray(items) ? items : []);
        setCategoriesLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredOptions = distributorsOptions;

  // const distributorFiltered = distributorsOptions.filter((distributor) => {
  //   return distributor.users.some((user) => user.fasara === true);
  // });

  // const filteredOptions = distributorFiltered.filter((option) => {
  //   const isNLorBE = country === 'NL' || country === 'BE';

  //   if (isNLorBE == true) {
  //     return (
  //       !option.label.toLowerCase().includes('3m') &&
  //       !option.label.toLowerCase().includes('test') &&
  //       !option.label.toLowerCase().includes('distributor') &&
  //       !/^[a-z]+distributor/.test(option.label.toLowerCase())
  //     );
  //   } else {
  //     return (
  //       !option.label.toLowerCase().includes('test') &&
  //       !option.label.toLowerCase().includes('distributor') &&
  //       !/^[a-z]+distributor/.test(option.label.toLowerCase())
  //     );
  //   }
  // });

  const rawApplications = get(values, 'data.applications');
  const applications = Array.isArray(rawApplications) ? rawApplications : [];
  const theDistributor = applications?.[0]?.distributor;

  return (
    <FieldArray name="data.applications">
      {({ push, remove }) => (
        <>
          {applications.map((_, index) => (
            <FasaraWinApplicationRow
              key={index}
              index={index}
              values={values}
              categoriesOptions={categoriesOptions}
              categoriesLoading={categoriesLoading}
              filteredOptions={filteredOptions}
              onRemove={() => remove(index)}
              showRemoveButton={applications.length > 1}
            />
          ))}
          {applications.length < 4 && (
            <>
              <div className="kt-space-20" />
              <div className="row">
                <div className="col-12">
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() =>
                      push(newSheet({ distributor: theDistributor }))
                    }
                  >
                    <FormattedMessage id="Agregar nueva lámina" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </FieldArray>
  );
};

const FasaraWinApplications = connect(
  fasaraWinSheetsMapStateToProps,
  fasaraWinSheetsMapDispatchToProps,
)(FasaraWinApplicationsInner);

export const Sheets = ({ values, country }) => {
  return (
    <>
      <PorletHeaderTitle variant="h5">
        <FormattedMessage id="3M Glass Finishes" />
      </PorletHeaderTitle>
      <FasaraWinApplications values={values} country={country} />
      <div className="kt-space-20" />

      <div className="kt-space-20" />
    </>
  );
};

export const mapStateToProps = (state) => ({
  country: getCountryProfile(state),
});

export default connect(mapStateToProps)(Sheets);
